import { useState, useMemo } from "react";
import { NerdStackFooter } from "../components/NerdStackFooter.jsx";
import { ChemBackButton } from "./ChemBackButton.jsx";

const EM = "#F8B84E", EM_DEEP = "#7C3AED", EM_LIGHT = "#FCD9A0";
const rgba = (hex, a) => {
  const r = parseInt(hex.slice(1,3),16), g = parseInt(hex.slice(3,5),16), b = parseInt(hex.slice(5,7),16);
  return `rgba(${r},${g},${b},${a})`;
};

// M1·V1 = M2·V2. Given any 3, solve the 4th. Pure algebra, exact.
const FIELDS = [
  { k: "M1", label: "Initial concentration (M₁)", unit: "M" },
  { k: "V1", label: "Initial volume (V₁)", unit: "L" },
  { k: "M2", label: "Final concentration (M₂)", unit: "M" },
  { k: "V2", label: "Final volume (V₂)", unit: "L" },
];

export default function Dilution() {
  const [vals, setVals] = useState({ M1: "", V1: "", M2: "", V2: "" });

  const result = useMemo(() => {
    const n = {};
    for (const f of FIELDS) {
      const v = parseFloat(vals[f.k]);
      n[f.k] = vals[f.k] === "" ? null : (isFinite(v) ? v : NaN);
    }
    const filled = FIELDS.filter(f => n[f.k] !== null);
    if (filled.length !== 3) return null;
    if (filled.some(f => isNaN(n[f.k]))) return null;
    // Concentrations and volumes are physically positive. Reject a
    // negative/zero filled input even when the solved 4th comes out
    // positive (e.g. V1=-1, M2=-2, V2=1 → M1=2). Catch it before solving.
    if (filled.some(f => n[f.k] <= 0))
      return { err: "Concentrations and volumes must be positive numbers" };
    const missing = FIELDS.find(f => n[f.k] === null).k;
    let solved;
    try {
      if (missing === "M1") { if (n.V1 === 0) return { err: "V₁ can't be 0" }; solved = (n.M2 * n.V2) / n.V1; }
      else if (missing === "V1") { if (n.M1 === 0) return { err: "M₁ can't be 0" }; solved = (n.M2 * n.V2) / n.M1; }
      else if (missing === "M2") { if (n.V2 === 0) return { err: "V₂ can't be 0" }; solved = (n.M1 * n.V1) / n.V2; }
      else { if (n.M2 === 0) return { err: "M₂ can't be 0" }; solved = (n.M1 * n.V1) / n.M2; }
    } catch { return null; }
    if (!isFinite(solved) || solved < 0) return { err: "No valid positive solution" };
    return { missing, solved };
  }, [vals]);

  const set = (k, v) => setVals(s => ({ ...s, [k]: v }));
  const inStyle = (active) => ({ width: "100%", padding: "13px 15px", borderRadius: 10, border: active ? `1.5px solid ${rgba(EM,0.4)}` : `1px solid ${rgba(EM,0.15)}`, background: active ? rgba(EM,0.06) : "rgba(0,0,0,0.3)", color: active ? EM_LIGHT : "#E2E8F0", fontSize: 16, fontFamily: "inherit", outline: "none", boxSizing: "border-box", fontWeight: active ? 700 : 400 });

  return (
    <div style={{ minHeight: "100vh", background: "#0A0B14", fontFamily: "'DM Sans', system-ui, sans-serif", color: "#E2E8F0" }}>
      <div style={{ position: "fixed", top: "-25%", left: "-10%", width: "50vw", height: "50vw", background: `radial-gradient(circle, ${rgba(EM,0.05)} 0%, transparent 65%)`, pointerEvents: "none" }} />
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 24px", paddingTop: "calc(16px + env(safe-area-inset-top))", paddingLeft: "calc(24px + env(safe-area-inset-left))", paddingRight: "calc(24px + env(safe-area-inset-right))", borderBottom: `1px solid ${rgba(EM,0.06)}`, position: "relative", zIndex: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <ChemBackButton accentColor={EM} />
          <div style={{ width: 36, height: 36, borderRadius: 10, background: `linear-gradient(135deg, ${EM}, ${EM_DEEP})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 800, color: "#fff" }}>Ch</div>
          <span style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.5px", background: `linear-gradient(135deg, ${EM_LIGHT}, #A7F3D0)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Dilution Calculator</span>
          <span style={{ fontSize: 11, color: "#374151", marginLeft: 4 }}>by NerdChem</span>
        </div>
        <div style={{ fontSize: 12, color: "#374151" }}>100% free</div>
      </header>

      <main style={{ maxWidth: 520, margin: "0 auto", padding: "32px 20px 80px", position: "relative", zIndex: 10 }}>
        <p style={{ fontSize: 15, color: "#9CA3AF", lineHeight: 1.6, textAlign: "center", margin: "0 0 8px" }}>
          Solves the dilution equation. Fill in any three values and the fourth
          is calculated automatically.
        </p>
        <div style={{ textAlign: "center", fontSize: 22, fontWeight: 700, color: EM_LIGHT, margin: "0 0 26px", letterSpacing: "1px" }}>M₁V₁ = M₂V₂</div>

        <div style={{ display: "grid", gap: 14 }}>
          {FIELDS.map(f => {
            const isSolved = result && !result.err && result.missing === f.k;
            return (
              <div key={f.k}>
                <label style={{ fontSize: 11, fontWeight: 600, color: "#6B7280", textTransform: "uppercase", letterSpacing: "0.5px" }}>{f.label}</label>
                <div style={{ position: "relative", marginTop: 6 }}>
                  <input
                    type="number"
                    value={isSolved ? result.solved.toPrecision(5).replace(/\.?0+$/, "") : vals[f.k]}
                    onChange={(e) => set(f.k, e.target.value)}
                    readOnly={isSolved}
                    placeholder={isSolved ? "" : `— ${f.unit} —`}
                    style={inStyle(isSolved)}
                  />
                  <span style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", fontSize: 13, color: "#6B7280", pointerEvents: "none" }}>{f.unit}</span>
                </div>
              </div>
            );
          })}
        </div>

        {result?.err && (
          <div style={{ marginTop: 22, padding: "14px 18px", borderRadius: 12, background: "rgba(244,63,94,0.08)", border: "1px solid rgba(244,63,94,0.25)", color: "#FDA4AF", fontSize: 14, textAlign: "center" }}>{result.err}</div>
        )}
        {result && !result.err && (
          <div style={{ marginTop: 22, padding: "18px", borderRadius: 14, background: rgba(EM,0.07), border: `1px solid ${rgba(EM,0.25)}`, textAlign: "center", fontSize: 14, color: EM_LIGHT }}>
            Solved for <strong>{result.missing.replace("1","₁").replace("2","₂")}</strong> — highlighted above.
          </div>
        )}
        <p style={{ fontSize: 12, color: "#4B5563", textAlign: "center", marginTop: 18, lineHeight: 1.5 }}>
          Units are consistent on both sides — use the same volume unit for V₁ and
          V₂ (L or mL), and the same concentration unit for M₁ and M₂.
        </p>
      </main>

      <footer style={{ textAlign: "center", padding: 24, fontSize: 12, color: "#1F2937", borderTop: `1px solid ${rgba(EM,0.04)}` }}>
        <NerdStackFooter variant="tool" family="chem" />
      </footer>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap'); input::placeholder { color: #4B5563; } input[type=number]::-webkit-inner-spin-button{-webkit-appearance:none;} * { box-sizing: border-box; } body { margin: 0; }`}</style>
    </div>
  );
}
